/**
 * The Refine section on the profile page: a career adviser you argue with about
 * the rubric shown above it. `POST /api/profile/refine`.
 *
 * The onboarding interview runs once, before the candidate has seen a single
 * match. This runs afterwards, and the server hands the model the jobs they
 * marked interested or not interested — so a rule like "score this low even at
 * a premium brand" has somewhere to come from. Every turn that changes the
 * rubric saves it.
 *
 * **Trap: `changed` is the truth, the reply is a claim.** The server diffs the
 * stored rubric against the rewritten one, so `changed` reports what actually
 * moved even when the model's prose says otherwise. Render both, and let the
 * list win: a turn with an empty `changed` saved nothing, however confident the
 * reply sounds.
 *
 * **PIVOT-109: the diff is a ticklist, not a sentence.** A turn that changed
 * the rubric renders `RubricDiff` — added/dropped lines per field, before →
 * after for everything else — computed here from the rubric captured at send
 * time (`rubric` prop) against the server's post-turn rubric. Undo is threaded
 * up through `onUndoField` to the hosting page, which performs the actual
 * `PUT /api/profile` save; this component never fakes a save locally.
 *
 * The transcript lives here and nowhere else. The rubric is the state that
 * matters and it is on the server after every turn, so a reload costs the
 * conversation and no work — unlike `InterviewChat`, whose transcript IS the
 * unsaved work and is therefore stored server-side. That also means a turn's
 * diff (and its undo buttons) cannot outlive the turn: both live in the same
 * `turns` state, so a hard refresh clears them together rather than leaving a
 * button with nothing left to undo.
 */

import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { refineProfile, type RefineTurn } from '../api/onboarding'
import { isAbortError } from '../api/client'
import { useI18n } from '../i18n'
import type { Rubric, RubricField } from '../types/domain'
import { RubricDiff } from './RubricDiff'
import { Thinking } from './Thinking'
import './RefineChat.css'

/**
 * The server bounds `history` at 12 turns and drops the overflow. Trimming to
 * the same number here means the adviser sees the *recent* turns rather than
 * whichever twelve happened to be at the front of the array.
 */
const MAX_HISTORY = 12

interface Turn extends RefineTurn {
  /**
   * The diff this assistant turn produced. Undefined on a user turn, and on
   * an assistant turn that talked without saving `changed` is simply empty —
   * both `changed`/`before`/`after` travel together so a turn can never have
   * one without the others to render it from.
   */
  diff?: {
    changed: RubricField[]
    /** The rubric as it stood right before this turn was sent. */
    before: Rubric
    /** The rubric the server returned after this turn. */
    after: Rubric
    /** Fields undone since. Grows only; a turn is never re-applied. */
    undoneFields: RubricField[]
  }
}

interface RefineChatProps {
  /** The rubric as it stands right now, so a turn about to be sent can
      capture its own "before" — the parent already owns this state and
      threads it back out through `onRubricChanged`. */
  rubric: Rubric
  /** Called with the stored rubric after a turn that changed it, or after a
      successful undo. */
  onRubricChanged: (rubric: Rubric) => void
  /**
   * Restore one field of one turn's diff to `revertTo`, via a real save
   * (PIVOT-109 Interface — "a real save, not client state"). The hosting page
   * implements this against `PUT /api/profile`; resolves with the server's
   * rubric on success, rejects on failure so the row stays un-undone and the
   * page's own error copy is what the candidate sees.
   */
  onUndoField: (field: RubricField, revertTo: Rubric[RubricField]) => Promise<Rubric>
}

export function RefineChat({ rubric, onRubricChanged, onUndoField }: RefineChatProps) {
  const { t } = useI18n()
  const [message, setMessage] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(false)

  // Closes the request if the page unmounts mid-answer; otherwise it stays open
  // and lands on a component nobody can see.
  const inFlight = useRef<AbortController | null>(null)
  useEffect(() => () => inFlight.current?.abort(), [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const text = message.trim()
    if (!text || sending) return

    const controller = new AbortController()
    inFlight.current = controller
    setSending(true)
    setError(false)

    // Captured now, not read from the prop again after the await: a second
    // turn sent while this one is still in flight would otherwise see the
    // first turn's *outcome* as its own "before", collapsing two diffs into one.
    const before = rubric

    // The sent turn goes up immediately; `history` is what preceded it, since
    // the server takes this message separately from the conversation so far.
    const history = turns.slice(-MAX_HISTORY).map(({ role, content }) => ({ role, content }))
    setTurns((prev) => [...prev, { role: 'user', content: text }])
    setMessage('')

    try {
      const result = await refineProfile(text, history, controller.signal)
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.reply,
          diff: { changed: result.changed, before, after: result.rubric, undoneFields: [] },
        },
      ])
      if (result.changed.length > 0) onRubricChanged(result.rubric)
    } catch (reason) {
      if (isAbortError(reason)) return
      // Put the message back in the box: retyping a paragraph because the
      // server hiccuped is the worst thing this screen could do.
      setMessage(text)
      setTurns((prev) => prev.slice(0, -1))
      setError(true)
    } finally {
      if (inFlight.current === controller) {
        inFlight.current = null
        setSending(false)
      }
    }
  }

  /**
   * One field's undo, for one turn's diff. `onUndoField` performs the actual
   * save and either resolves with the server's rubric (mark the row undone,
   * hand the new rubric to the parent) or rejects (leave the row as it was —
   * the parent already put its own error copy on screen). Either way this
   * turn's `diff.before` is untouched, so tapping undo again would ask for
   * the exact same revert.
   */
  const handleUndo = async (turnIndex: number, field: RubricField) => {
    const turn = turns[turnIndex]
    if (!turn.diff) return
    const newRubric = await onUndoField(field, turn.diff.before[field])
    setTurns((prev) =>
      prev.map((entry, i) =>
        i === turnIndex && entry.diff
          ? { ...entry, diff: { ...entry.diff, undoneFields: [...entry.diff.undoneFields, field] } }
          : entry,
      ),
    )
    onRubricChanged(newRubric)
  }

  return (
    <section className="refine-chat" id="refine">
      <h2>{t('profile.refineTitle')}</h2>
      <p className="refine-chat-intro">{t('profile.refineBody')}</p>

      <ol className="refine-chat-log">
        <li className="refine-chat-turn refine-chat-turn--adviser">
          <span className="refine-chat-who">{t('profile.refineAdviser')}</span>
          <p>{t('profile.refineOpener')}</p>
        </li>
        {turns.map((turn, i) => (
          <li
            // Turns are append-only and never reordered, so the index is stable
            // for the life of the list.
            key={i}
            className={`refine-chat-turn refine-chat-turn--${turn.role === 'user' ? 'you' : 'adviser'}`}
          >
            <span className="refine-chat-who">
              {turn.role === 'user' ? t('profile.refineYou') : t('profile.refineAdviser')}
            </span>
            <p>{turn.content}</p>
            {turn.role === 'assistant' &&
              turn.diff &&
              (turn.diff.changed.length > 0 ? (
                <RubricDiff
                  changed={turn.diff.changed}
                  before={turn.diff.before}
                  after={turn.diff.after}
                  undoneFields={turn.diff.undoneFields}
                  onUndo={(field) => handleUndo(i, field)}
                />
              ) : (
                <p className="refine-chat-note">{t('profile.refineNoChange')}</p>
              ))}
          </li>
        ))}
      </ol>

      {sending && <Thinking label="profile.refining" patience="profile.refiningPatience" />}
      {error && <p className="form-error">{t('common.error')}</p>}

      <form onSubmit={handleSubmit} className="refine-chat-form">
        <input
          type="text"
          value={message}
          placeholder={t('profile.refinePlaceholder')}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending}
        />
        <button type="submit" className="btn btn-primary" disabled={sending || !message.trim()}>
          {t('profile.refineSend')}
        </button>
      </form>
    </section>
  )
}
